import {
  onBeforeUnmount,
  onMounted,
} from 'vue';

import {
  EKeyboardKey,
} from '../constants/keyboard.ts';
import {
  KEYBOARD_AREA_ATTRIBUTE,
  activeKeyboardArea,
  findKeyboardAreaElement,
} from '../utils/keyboard-area.ts';
import {
  isEditableElement,
  isNothingFocused,
} from '../utils/keyboard-target.ts';

const TABLE_SELECTOR = '.p-datatable';
const BODY_ROW_SELECTOR = '.p-datatable-tbody > tr:not(.p-datatable-emptymessage):not(.p-datatable-row-expansion)';
// A lazy table first renders placeholder rows and then the loaded ones, so the first row is focused
// again once the rows stop changing, until the page has had enough time to load.
const PAGE_RENDER_SETTLE_TIME_IN_MILLISECONDS = 150;
const PAGE_LOAD_TIMEOUT_IN_MILLISECONDS = 5000;

/** The rows of the table itself, without those of tables nested in its rows. */
function getBodyRowList(table: Element): HTMLTableRowElement[] {
  return Array.from(table.querySelectorAll<HTMLTableRowElement>(BODY_ROW_SELECTOR))
    .filter((row) => row.closest(TABLE_SELECTOR) === table && row.checkVisibility());
}

function getPaginatorButton(table: Element, key: EKeyboardKey.ArrowLeft | EKeyboardKey.ArrowRight): HTMLButtonElement | undefined {
  const selector = key === EKeyboardKey.ArrowLeft
    ? '.p-paginator-prev'
    : '.p-paginator-next';
  return Array.from(table.querySelectorAll<HTMLButtonElement>(selector))
    .find((button) => button.closest(TABLE_SELECTOR) === table && !button.disabled);
}

function focusRow(row: HTMLTableRowElement) {
  if (!row.hasAttribute('tabindex')) {
    row.tabIndex = -1;
  }
  row.focus({
    preventScroll: true,
  });
  row.scrollIntoView({
    block: 'nearest',
  });
}

function focusFirstRowWhileThePageLoads(table: Element) {
  let settleTimeout: ReturnType<typeof setTimeout> | undefined;
  const observer = new MutationObserver(() => {
    clearTimeout(settleTimeout);
    settleTimeout = setTimeout(() => {
      const isFocusLost = document.activeElement === null || document.activeElement === document.body;
      const firstRow = getBodyRowList(table)[0];
      if (isFocusLost && firstRow) {
        focusRow(firstRow);
      }
    }, PAGE_RENDER_SETTLE_TIME_IN_MILLISECONDS);
  });
  observer.observe(table, {
    childList: true,
    subtree: true,
  });
  setTimeout(() => {
    observer.disconnect();
    clearTimeout(settleTimeout);
  }, PAGE_LOAD_TIMEOUT_IN_MILLISECONDS);
}

/**
 * Lets every data table be used with the keyboard: the up and down arrow keys move between its rows,
 * the left and right arrow keys turn its pages, `Enter` follows the first link of a row and `Space`
 * toggles the checkbox of a row. The keys apply to the table that has the focus or, while nothing
 * has the focus, to the table clicked last. The up and down keys also apply to the only table on
 * the page, while the left and right keys are left to the page, e.g. to shift its date range.
 * Without focus, only tables in the area of the app layout that the keyboard is switched to count.
 */
export function useTableKeyboardNavigation() {
  let lastClickedTable: Element | undefined;
  let lastClickedRow: HTMLTableRowElement | undefined;

  function rememberClickedTable(event: PointerEvent) {
    if (!(event.target instanceof Element)) {
      return;
    }
    lastClickedTable = event.target.closest(TABLE_SELECTOR) ?? undefined;
    lastClickedRow = event.target.closest<HTMLTableRowElement>(BODY_ROW_SELECTOR) ?? undefined;
  }

  /**
   * Where the keys look for a table while nothing has the focus: in the area of the app layout that
   * has the focus as a whole or was focused or clicked last, or on the whole page without areas.
   */
  function findTableScope(target: Element): Element | Document {
    if (target.hasAttribute(KEYBOARD_AREA_ATTRIBUTE)) {
      return target;
    }
    return findKeyboardAreaElement(activeKeyboardArea.value) ?? document;
  }

  function findTableWithoutFocus(isOnlyTableAllowed: boolean, scope: Element | Document): Element | undefined {
    if (lastClickedTable?.isConnected && lastClickedTable.checkVisibility() && scope.contains(lastClickedTable)) {
      return lastClickedTable;
    }
    if (!isOnlyTableAllowed) {
      return undefined;
    }
    const visibleTableList = Array.from(scope.querySelectorAll(TABLE_SELECTOR))
      .filter((table) => table.checkVisibility() && !table.parentElement?.closest(TABLE_SELECTOR));
    return visibleTableList.length === 1
      ? visibleTableList[0]
      : undefined;
  }

  function findTableAndRow(target: Element, isOnlyTableAllowed: boolean): {
    table: Element,
    row: HTMLTableRowElement | undefined,
  } | undefined {
    if (isNothingFocused(target)) {
      const table = findTableWithoutFocus(isOnlyTableAllowed, findTableScope(target));
      if (!table) {
        return undefined;
      }
      const row = lastClickedRow?.isConnected && lastClickedRow.closest(TABLE_SELECTOR) === table
        ? lastClickedRow
        : undefined;
      return {
        table,
        row,
      };
    }
    const table = target.closest(TABLE_SELECTOR);
    // The header has keys of its own, e.g. for sorting, and the filters of the columns.
    if (!table || target.closest('.p-datatable-thead')) {
      return undefined;
    }
    return {
      table,
      row: target.closest<HTMLTableRowElement>(BODY_ROW_SELECTOR) ?? undefined,
    };
  }

  function moveToRow(event: KeyboardEvent, table: Element, row: HTMLTableRowElement | undefined) {
    const rowList = getBodyRowList(table);
    const rowIndex = row
      ? rowList.indexOf(row)
      : -1;
    let nextIndex: number;
    if (event.key === EKeyboardKey.ArrowDown) {
      nextIndex = rowIndex + 1;
    } else {
      nextIndex = rowIndex === -1
        ? rowList.length - 1
        : rowIndex - 1;
    }
    const nextRow = rowList[nextIndex];
    if (!nextRow) {
      return;
    }
    event.preventDefault();
    focusRow(nextRow);
  }

  function turnPage(event: KeyboardEvent & {
    key: EKeyboardKey.ArrowLeft | EKeyboardKey.ArrowRight,
  }, table: Element) {
    const paginatorButton = getPaginatorButton(table, event.key);
    if (!paginatorButton) {
      return;
    }
    event.preventDefault();
    paginatorButton.click();
    const firstRow = getBodyRowList(table)[0];
    if (firstRow) {
      focusRow(firstRow);
    }
    focusFirstRowWhileThePageLoads(table);
  }

  function activateRow(event: KeyboardEvent, row: HTMLTableRowElement) {
    const control = event.key === EKeyboardKey.Enter
      ? row.querySelector<HTMLElement>('a[href]')
      : row.querySelector<HTMLElement>('input[type="checkbox"]');
    if (!control) {
      return;
    }
    event.preventDefault();
    control.click();
  }

  function navigateTableOnKey(event: KeyboardEvent) {
    const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (event.defaultPrevented || hasModifier || !(event.target instanceof Element) || isEditableElement(event.target)) {
      return;
    }
    const isPageKey = event.key === EKeyboardKey.ArrowLeft || event.key === EKeyboardKey.ArrowRight;
    const tableAndRow = findTableAndRow(event.target, !isPageKey);
    if (!tableAndRow) {
      return;
    }
    const {
      table,
      row,
    } = tableAndRow;
    switch (event.key) {
      case EKeyboardKey.ArrowDown:
      case EKeyboardKey.ArrowUp:
        moveToRow(event, table, row);
        break;
      case EKeyboardKey.ArrowLeft:
      case EKeyboardKey.ArrowRight:
        turnPage(event as KeyboardEvent & {
          key: EKeyboardKey.ArrowLeft | EKeyboardKey.ArrowRight,
        }, table);
        break;
      case EKeyboardKey.Enter:
      case EKeyboardKey.Space:
        // Only a focused row, as the keys keep their meaning for the links and buttons in it.
        if (row && event.target === row) {
          activateRow(event, row);
        }
        break;
    }
  }

  onMounted(() => {
    document.addEventListener('pointerdown', rememberClickedTable, true);
    document.addEventListener('keydown', navigateTableOnKey);
  });
  onBeforeUnmount(() => {
    document.removeEventListener('pointerdown', rememberClickedTable, true);
    document.removeEventListener('keydown', navigateTableOnKey);
  });
}
